import React, { useState, useRef, useEffect } from "react";
import { Send, Loader2 } from "lucide-react";

interface ChatTextAreaProps {
  onSendMessage: (message: string) => void;
  isLoading: boolean;
  placeholder?: string;
  disabled?: boolean;
}

export function ChatTextArea({ onSendMessage, isLoading, placeholder, disabled }: ChatTextAreaProps) {
  const [value, setValue] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!value.trim() || isLoading || disabled) return;
    onSendMessage(value);
    setValue("");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  // Auto-resize
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [value]);

  return (
    <div className="p-4 bg-white">
      <div className="relative flex items-end gap-2 p-3 border border-[#430ea2] rounded-3xl focus-within:shadow-[0_0_0_4px_rgba(74,16,180,0.15)]">
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder || "Ask a question..."}
          className="w-full max-h-[150px] bg-transparent resize-none outline-none min-h-[24px] py-1"
          rows={1}
          disabled={isLoading || disabled}
        />
        <button
          onClick={() => handleSubmit()}
          disabled={!value.trim() || isLoading || disabled}
          className="px-4 py-2 rounded-2xl text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:brightness-90"
          style={{ background: 'linear-gradient(180deg, #3448AE 0%, #6015E9 100%)' }}
        >
          {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>
      <div className="text-xs text-gray-500 mt-2 text-center">
        AI can make mistakes. Please verify important information.
      </div>
    </div>
  );
}
